package com.tovtalk.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.VerticalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.tovtalk.data.analytics.AnalyticsClient
import com.tovtalk.data.analytics.InteractionType
import com.tovtalk.di.getKoin
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.FeedItem
import com.tovtalk.domain.entity.KosherLevel
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.utils.constants.AppConstants
import com.tovtalk.utils.deeplink.DeepLinkBuilder
import androidx.compose.foundation.background
import com.tovtalk.ui.theme.TovTalkTheme
import androidx.compose.ui.graphics.Color
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ReelFeedContent(
    feedItems: List<FeedItem>,
    initialPage: Int = 0,
    targetPostId: Int? = null,
    currentUserId: Int? = null,
    isGuestMode: Boolean = false,
    audioAutoplay: Boolean = true,
    onToggleMute: () -> Unit = {},
    bottomPadding: Dp = 0.dp,
    adsRepository: AdsRepository? = null,
    // Post action callbacks
    onLikePost: (postId: Int, isLiked: Boolean) -> Unit,
    onSavePost: (postId: Int, isSaved: Boolean) -> Unit = { _, _ -> },
    onFollowUser: (userId: Int) -> Unit = {},
    onCommentAdded: (postId: Int) -> Unit = {},
    // Navigation
    onUserClick: (userId: Int, currentUserId: Int?, postId: Int) -> Unit,
    onNavigateToProfile: (Int) -> Unit = {},
    onHashtagClick: (String) -> Unit = {},
    // Lifecycle
    onPageChanged: (Int) -> Unit = {},
    onViewCountReached: (postId: Int) -> Unit = {},
    onLoadMore: () -> Unit = {},
    // Guest mode
    onGuestActionRequired: (() -> Unit)? = null,
    onShowGuestPromptSheet: (() -> Unit)? = null,
    onShowGuestReportPrompt: (() -> Unit)? = null,
    showReactionIcon: Boolean = true,
    onNavigateToStories: ((Int) -> Unit)? = null,
    // PERF(1): when the caller already knows whether text comments are allowed
    // (e.g. HomeFeedContent computes it from kosher levels), it passes the value
    // here so ReelFeedContent does NOT fetch getKosherLevels() a second time on the
    // feed path. When null (e.g. ProfileFeedScreen), we fetch it ourselves as before.
    commentsEnabledOverride: Boolean? = null,
    // Extra content slots
    topContent: @Composable () -> Unit = {},
) {
    var showCommentsForPostId by remember { mutableStateOf<Int?>(null) }
    var sharePostRef by remember { mutableStateOf<String?>(null) }
    var reportPostId by remember { mutableStateOf<Int?>(null) }

    val analytics: AnalyticsClient = koinInject()
    val appRepository: AppRepository = remember { getKoin().get() }
    val userRepository: UserRepository = remember { getKoin().get() }
    val cachedUser by userRepository.getCachedUser().collectAsState(initial = null)
    var kosherLevels by remember { mutableStateOf<List<KosherLevel>>(emptyList()) }
    // PERF(1): only fetch getKosherLevels() when the caller did NOT already supply
    // commentsEnabled. HomeFeedContent computes the same value and passes it via
    // commentsEnabledOverride, so this avoids the duplicate network call on the feed
    // path. Composables above stay unconditional (Rules of Composition); only the
    // network fetch is gated. The key on commentsEnabledOverride is stable per caller.
    LaunchedEffect(commentsEnabledOverride) {
        if (commentsEnabledOverride == null) {
            when (val result = appRepository.getKosherLevels()) {
                is DomainResult.Success -> kosherLevels = result.data
                else -> {} // keep empty — commentsEnabled defaults to true
            }
        }
    }
    val commentsEnabled = remember(commentsEnabledOverride, kosherLevels, cachedUser) {
        commentsEnabledOverride
            ?: (kosherLevels.find { it.id == cachedUser?.kashrutLevel }?.allowTextComments ?: true)
    }

    val pagerState = rememberPagerState(
        initialPage = initialPage.coerceIn(0, (feedItems.size - 1).coerceAtLeast(0)),
        pageCount = { feedItems.size }
    )

    // Scroll to target post when posts load/change
    LaunchedEffect(feedItems, targetPostId) {
        if (targetPostId != null && feedItems.isNotEmpty()) {
            val targetIndex = feedItems.indexOfFirst { (it as? com.tovtalk.domain.entity.FeedItem.VideoItem)?.post?.id == targetPostId }
            com.tovtalk.utils.logger.Logger.d("FEED_DEBUG: ReelFeedContent scroll: targetPostId=$targetPostId, targetIndex=$targetIndex, currentPage=${pagerState.currentPage}, itemsSize=${feedItems.size}", "ReelFeedContent")
            if (targetIndex >= 0 && pagerState.currentPage != targetIndex) {
                com.tovtalk.utils.logger.Logger.d("FEED_DEBUG: scrollToPage($targetIndex)", "ReelFeedContent")
                pagerState.scrollToPage(targetIndex)
            }
        }
    }
    val scope = rememberCoroutineScope()

    // PERF(5): this effect is keyed only on pagerState (intentionally — re-keying on
    // feedItems would relaunch the snapshotFlow collector on every append and could skip
    // page events). But it previously closed over the feedItems / callbacks captured at
    // launch, reading a stale list size when more items loaded. rememberUpdatedState lets
    // the long-lived collector always read the LATEST values without restarting.
    val latestFeedItems by rememberUpdatedState(feedItems)
    val latestOnPageChanged by rememberUpdatedState(onPageChanged)
    val latestOnLoadMore by rememberUpdatedState(onLoadMore)
    val latestOnViewCountReached by rememberUpdatedState(onViewCountReached)

    // Page change via snapshotFlow — guaranteed to capture every page, no cancellation skips.
    LaunchedEffect(pagerState) {
        snapshotFlow { pagerState.currentPage }.collect { currentPage ->
            latestOnPageChanged(currentPage)
            // Load more when near end
            if (currentPage >= latestFeedItems.size - 3) {
                latestOnLoadMore()
            }
            // View count tracking — only for video items
            (latestFeedItems.getOrNull(currentPage) as? FeedItem.VideoItem)?.post?.let { post ->
                delay(AppConstants.VIEW_COUNT_DELAY_MS)
                if (pagerState.currentPage == currentPage) {
                    latestOnViewCountReached(post.id)
                }
            }
        }
    }

    Box(modifier = Modifier.fillMaxSize().background(TovTalkTheme.colors.videoBackground)) {
        VerticalPager(
            state = pagerState,
            modifier = Modifier.fillMaxSize().clipToBounds().background(TovTalkTheme.colors.videoBackground),
            beyondViewportPageCount = 2,
            pageSpacing = 0.dp,
            key = { page ->
                when (val item = feedItems.getOrNull(page)) {
                    is FeedItem.VideoItem -> item.post.id
                    is FeedItem.NativeAdItem -> "ad_${item.slotIndex}"
                    is FeedItem.VideoAdItem -> "video_ad_${item.slotIndex}"
                    null -> page
                }
            }
        ) { page ->
            val isCurrentPage = pagerState.currentPage == page
            // Mount the player for immediate neighbors too (|distance| == 1) so
            // the next/prev video is already buffered + first frame rendered
            // when the user swipes — no thumbnail flash between videos.
            val distanceFromCurrent = kotlin.math.abs(pagerState.currentPage - page)
            val isPrerendering = !isCurrentPage && distanceFromCurrent == 1
            Box(modifier = Modifier.fillMaxSize().background(TovTalkTheme.colors.videoBackground)) {
                when (val item = feedItems.getOrNull(page)) {
                    is FeedItem.VideoItem -> {
                        val post = item.post
                        val totalPages = feedItems.size
                        val nextPost = (feedItems.getOrNull(page + 1) as? FeedItem.VideoItem)?.post
                        val nextVideoUrl = if (isCurrentPage && nextPost != null) nextPost.videoUrl else null

                        ReelItem(
                            post = post,
                            isVisible = isCurrentPage,
                            isPrerendering = isPrerendering,
                            nextVideoUrl = nextVideoUrl,
                            isPausedByComments = showCommentsForPostId != null,
                            currentUserId = currentUserId,
                            videoMuted = !audioAutoplay,
                            showMuteButton = true,
                            onToggleMute = onToggleMute,
                            isGuestMode = isGuestMode,
                            onGuestActionRequired = onGuestActionRequired,
                            onShowGuestPromptSheet = onShowGuestPromptSheet,
                            onLikeClick = { onLikePost(post.id, post.viewerState.isLiked) },
                            onCommentClick = {
                                if (isGuestMode && showReactionIcon) {
                                    onGuestActionRequired?.invoke()
                                } else if (commentsEnabled) {
                                    showCommentsForPostId = post.id
                                    // C9: COMMENT_PANEL_OPENED (non-critical)
                                    scope.launch {
                                        analytics.trackInteraction(
                                            postId = post.id.toLong(),
                                            type = InteractionType.COMMENT_PANEL_OPENED,
                                        )
                                    }
                                }
                            },
                            onShareClick = {
                                sharePostRef = post.ref
                                // C9: SHARE (critical — flushes immediately)
                                scope.launch {
                                    analytics.trackCriticalInteraction(
                                        postId = post.id.toLong(),
                                        type = InteractionType.SHARE,
                                        payload = mapOf("platform" to "unknown"),
                                    )
                                }
                            },
                            onReportClick = {
                                if (isGuestMode) onShowGuestReportPrompt?.invoke()
                                else reportPostId = post.id
                                // C9: REPORT is tracked inside ReportReelDialog after successful submit
                            },
                            onSaveClick = {
                                if (isGuestMode) onGuestActionRequired?.invoke()
                                else onSavePost(post.id, post.viewerState.isSaved)
                            },
                            onUserClick = {
                                // C9: PROFILE_CLICK (non-critical) — from video overlay only
                                scope.launch {
                                    analytics.trackInteraction(
                                        postId = post.id.toLong(),
                                        type = InteractionType.PROFILE_CLICK,
                                    )
                                }
                                onUserClick(post.author.id, currentUserId, post.id)
                            },
                            onFollowClick = {
                                if (isGuestMode) {
                                    scope.launch { delay(500); onShowGuestPromptSheet?.invoke() }
                                } else {
                                    // C9: FOLLOW_FROM_VIDEO (critical — flushes immediately)
                                    scope.launch {
                                        analytics.trackCriticalInteraction(
                                            postId = post.id.toLong(),
                                            type = InteractionType.FOLLOW_FROM_VIDEO,
                                        )
                                    }
                                    onFollowUser(post.author.id)
                                }
                            },
                            onVideoEnded = {
                                val nextPage = (page + 1).coerceAtMost(totalPages - 1)
                                if (nextPage != page) {
                                    scope.launch { pagerState.animateScrollToPage(nextPage) }
                                }
                            },
                            onHashtagClick = { hashtag ->
                                // C9: HASHTAG_CLICK (non-critical)
                                scope.launch {
                                    analytics.trackInteraction(
                                        postId = post.id.toLong(),
                                        type = InteractionType.HASHTAG_CLICK,
                                        payload = mapOf("hashtag" to hashtag),
                                    )
                                }
                                onHashtagClick(hashtag)
                            },
                            commentsEnabled = commentsEnabled,
                            showReactionIcon = showReactionIcon,
                            onNavigateToStories = onNavigateToStories,
                        )
                    }
                    is FeedItem.NativeAdItem -> {
                        if (adsRepository != null) {
                            NativeAdCard(
                                slotIndex = item.slotIndex,
                                adsRepository = adsRepository,
                                modifier = Modifier.fillMaxSize()
                            )
                        } else {
                            Box(modifier = Modifier.fillMaxSize())
                        }
                    }
                    is FeedItem.VideoAdItem -> {
                        if (adsRepository != null) {
                            SponsoredVideoReel(
                                slotIndex = item.slotIndex,
                                adsRepository = adsRepository,
                                isVisible = isCurrentPage,
                                muted = !audioAutoplay,
                                onAdvance = {
                                    val nextPage = (page + 1).coerceAtMost(feedItems.size - 1)
                                    if (nextPage != page) {
                                        scope.launch { pagerState.animateScrollToPage(nextPage) }
                                    }
                                },
                                modifier = Modifier.fillMaxSize(),
                            )
                        } else {
                            Box(modifier = Modifier.fillMaxSize())
                        }
                    }
                    null -> Box(modifier = Modifier.fillMaxSize())
                }
            }
        }

        topContent()

        // Dialogs/Sheets
        sharePostRef?.let { ref ->
            ShareBottomSheet(
                deepLinkUrl = DeepLinkBuilder.buildPostUrl(ref),
                onDismiss = { sharePostRef = null }
            )
        }
        reportPostId?.let { postId ->
            ReportReelDialog(
                postId = postId,
                onDismiss = { reportPostId = null },
                onReported = { reportPostId = null }
            )
        }
        showCommentsForPostId?.let { postId ->
            val postOwnerId = feedItems
                .filterIsInstance<FeedItem.VideoItem>()
                .firstOrNull { it.post.id == postId }
                ?.post?.author?.id ?: 0
            CommentsBottomSheet(
                postId = postId,
                postOwnerId = postOwnerId,
                onDismiss = { showCommentsForPostId = null },
                onNavigateToProfile = onNavigateToProfile,
                onCommentAdded = { onCommentAdded(postId) },
                isGuestMode = isGuestMode
            )
        }
    }
}
